import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError, uploadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowRight, CalendarDays, CalendarRange, CheckCircle2, ChevronDown, Download, Euro, Eye,
  File as FileIcon, Flag, History, Inbox, Link2, ListTodo, MessageSquare, MoreHorizontal,
  Paperclip, Plus, Search, Send, Trash2, UserRound, X, XCircle,
} from "lucide-react";
import {
  CLIENT_REQUEST_TYPES, clientRequestTypeLabel, clientRequestStatus, fmtEuro,
  DEMANDE_SERVICES, demandeService, KANBAN_COLS, kanbanCol, DEMANDE_PRIORITIES, demandePriority,
} from "@/lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";

const sel = "w-full text-xs border border-neutral-200 rounded-lg px-2.5 py-2 bg-white focus:outline-none focus:border-neutral-400";
const fmtD = (iso, full = true) => {
  if (!iso) return "—";
  try { return format(parseISO(iso.slice(0, 10)), full ? "d MMMM yyyy" : "d MMM yyyy", { locale: fr }); } catch { return iso; }
};
const relTime = (iso) => {
  if (!iso) return "";
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (h < 1) return "à l'instant";
  if (h < 24) return `il y a ${h} heure${h > 1 ? "s" : ""}`;
  const d = Math.floor(h / 24);
  return `il y a ${d} jour${d > 1 ? "s" : ""}`;
};
const initialsOf = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const fmtSize = (s) => (s ? `${(s / 1048576).toFixed(1).replace(".", ",")} Mo` : "");
const fmtMin = (min) => {
  const h = Math.floor((min || 0) / 60);
  const m = (min || 0) % 60;
  return h > 0 ? `${h} h ${String(m).padStart(2, "0")}` : `${m} min`;
};
const CLIENT_STATUS_LABELS = { a_faire: "À faire", en_cours: "En cours", termine: "Terminée", annule: "Annulée" };
const AVATAR_BG = ["bg-[#DBEAFE] text-[#1D4ED8]", "bg-[#FEF3C7] text-[#B45309]", "bg-[#EDE9FE] text-[#6D28D9]", "bg-[#DCFCE7] text-[#15803D]", "bg-[#FCE7F3] text-[#BE185D]"];
const avatarCls = (name = "") => AVATAR_BG[(name.charCodeAt(0) || 0) % AVATAR_BG.length];

const mondayISO = (isoDate) => {
  const d = new Date(isoDate + "T12:00:00");
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const projectUrl = (lp) => !lp ? "#" : lp.kind === "web" ? `/sites-web/projet/${lp.id}` : lp.kind === "graphisme" ? "/graphisme" : "/taches";

/* ================= Modale de chiffrage (existante) ================= */
const EMPTY_ITEM = { prestation: "", description: "", qty: 1, price_ht: "" };

function QuoteModal({ request, onClose, onSent }) {
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [tva, setTva] = useState("20");
  const [delay, setDelay] = useState("");
  const [comment, setComment] = useState("");
  const [estimatedMin, setEstimatedMin] = useState("");
  const [capacity, setCapacity] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [services, setServices] = useState([]);
  const [acompteType, setAcompteType] = useState("aucun");
  const [acompteValue, setAcompteValue] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!request) return;
    api.get("/accounting/quote-templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/accounting/services").then((r) => setServices(r.data)).catch(() => {});
  }, [request]);

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setItems(t.items.map((i) => ({ ...i })));
    setTva(t.tva_mode === "non_assujetti" ? "none" : String(t.tva_rate || 20));
    setDelay(t.delay || "");
    setComment(t.comment || "");
  };

  const pickService = (sid) => {
    const s = services.find((x) => x.id === sid);
    if (!s) return;
    const row = { prestation: s.name + (s.unit === "mois" ? " (mensuel)" : ""), description: s.description || "", qty: 1, price_ht: s.price_ht };
    setItems((a) => (a.length === 1 && !a[0].prestation ? [row] : [...a, row]));
  };

  const saveTemplate = async () => {
    const valid = items.filter((i) => i.prestation.trim());
    if (!templateName.trim() || !valid.length) { toast.error("Nom du modèle et prestations requis"); return; }
    try {
      await api.post("/accounting/quote-templates", {
        name: templateName.trim(), items: valid.map((i) => ({ ...i, qty: Number(i.qty) || 1, price_ht: Number(i.price_ht) || 0 })),
        tva_mode: nonAssujetti ? "non_assujetti" : "tva", tva_rate: nonAssujetti ? 0 : Number(tva) || 0,
        delay, comment,
      });
      toast.success(`Modèle « ${templateName.trim()} » enregistré`);
      setSavingTemplate(false); setTemplateName("");
      api.get("/accounting/quote-templates").then((r) => setTemplates(r.data)).catch(() => {});
    } catch { toast.error("Erreur lors de l'enregistrement du modèle"); }
  };

  useEffect(() => {
    const est = Number(estimatedMin);
    const dl = request?.deadline || request?.desired_date;
    if (!request || !est || !dl) { setCapacity(null); return; }
    const t = setTimeout(() => {
      api.post("/agenda/check-capacity", { estimated_minutes: est, deadline: dl })
        .then((r) => setCapacity(r.data)).catch(() => {});
    }, 400);
    return () => clearTimeout(t);
  }, [estimatedMin, request]);
  const setItem = (i, k) => (e) => setItems((arr) => arr.map((it, j) => (j === i ? { ...it, [k]: e.target.value } : it)));
  const nonAssujetti = tva === "none";
  const totalHT = items.reduce((a, i) => a + (Number(i.qty) || 0) * (Number(i.price_ht) || 0), 0);
  const totalTTC = nonAssujetti ? totalHT : totalHT * (1 + (Number(tva) || 0) / 100);

  const submit = async () => {
    const valid = items.filter((i) => i.prestation.trim() && Number(i.price_ht) > 0);
    if (!valid.length) { toast.error("Ajoutez au moins une prestation avec un prix"); return; }
    if (!estimatedMin || Number(estimatedMin) <= 0) { toast.error("Renseignez le temps estimé (minutes) — nécessaire pour la planification"); return; }
    setLoading(true);
    try {
      await api.post(`/client-requests/${request.id}/quote`, {
        items: valid.map((i) => ({ ...i, qty: Number(i.qty) || 1, price_ht: Number(i.price_ht) })),
        tva_mode: nonAssujetti ? "non_assujetti" : "taux",
        tva_rate: nonAssujetti ? 0 : Number(tva) || 0, delay, comment,
        estimated_minutes: Number(estimatedMin),
        acompte_type: acompteType, acompte_value: Number(acompteValue) || 0,
      });
      toast.success("Devis envoyé au client pour validation");
      onClose(); onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur lors de l'envoi"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={!!request} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="quote-modal" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Chiffrer — {request?.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {templates.length > 0 && (
            <div>
              <Label>À partir d'un modèle</Label>
              <select className={sel} value="" onChange={(e) => applyTemplate(e.target.value)} data-testid="quote-template-picker">
                <option value="">Choisir un modèle…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          {services.length > 0 && (
            <div>
              <Label>Bibliothèque de prestations</Label>
              <select className={sel} value="" onChange={(e) => pickService(e.target.value)} data-testid="quote-service-picker">
                <option value="">Ajouter une prestation enregistrée…</option>
                {services.map((s) => <option key={s.id} value={s.id}>{s.name} — {fmtEuro(s.price_ht)}{s.unit === "mois" ? " / mois" : ""}</option>)}
              </select>
            </div>
          )}
          {items.map((it, i) => (
            <div key={i} className="border border-neutral-200 rounded-lg p-3 space-y-2" data-testid={`quote-item-${i}`}>
              <div className="flex gap-2">
                <Input placeholder="Prestation (ex : Création 3 Reels)" value={it.prestation} onChange={setItem(i, "prestation")} className="flex-1" data-testid={`quote-prestation-${i}`} />
                {items.length > 1 && (
                  <button className="p-1.5 text-neutral-400 hover:text-red-600" onClick={() => setItems((a) => a.filter((_, j) => j !== i))}><X className="w-4 h-4" /></button>
                )}
              </div>
              <Input placeholder="Description (optionnel)" value={it.description} onChange={setItem(i, "description")} />
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Quantité</Label><Input type="number" min="0" step="0.5" value={it.qty} onChange={setItem(i, "qty")} className="mt-1" /></div>
                <div><Label className="text-xs">Prix unitaire HT (€)</Label><Input type="number" min="0" value={it.price_ht} onChange={setItem(i, "price_ht")} className="mt-1" data-testid={`quote-price-${i}`} /></div>
              </div>
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={() => setItems((a) => [...a, { ...EMPTY_ITEM }])} className="gap-1.5">
            <Plus className="w-3.5 h-3.5" /> Ajouter une prestation
          </Button>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>TVA</Label>
              <select data-testid="quote-tva" className={sel} value={tva} onChange={(e) => setTva(e.target.value)}>
                <option value="20">TVA 20 %</option>
                <option value="10">TVA 10 %</option>
                <option value="5.5">TVA 5,5 %</option>
                <option value="2.1">TVA 2,1 %</option>
                <option value="none">Non assujetti à la TVA</option>
              </select>
            </div>
            <div><Label>Délai annoncé</Label><Input className="mt-1.5" placeholder="Ex : 5 jours ouvrés" value={delay} onChange={(e) => setDelay(e.target.value)} /></div>
          </div>
          <div><Label>Commentaire</Label><textarea className={sel} rows={2} value={comment} onChange={(e) => setComment(e.target.value)} /></div>
          <div>
            <Label>Temps estimé (minutes) — interne, non visible par le client <span className="text-red-500">*</span></Label>
            <Input type="number" min="0" className="mt-1.5" placeholder="Ex : 120" value={estimatedMin}
              onChange={(e) => setEstimatedMin(e.target.value)} data-testid="quote-estimated-minutes" />
            {capacity && (
              <p className={`text-xs mt-1.5 ${capacity.ok ? "text-green-600" : "text-red-600"}`} data-testid="quote-capacity-hint">
                {capacity.ok
                  ? `Capacité OK : ${Math.floor(capacity.available_min / 60)} h ${String(capacity.available_min % 60).padStart(2, "0")} disponibles avant la deadline`
                  : `Capacité insuffisante : il manque ${Math.floor(capacity.missing_min / 60)} h ${String(capacity.missing_min % 60).padStart(2, "0")} avant la deadline`}
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Acompte demandé</Label>
              <select data-testid="quote-acompte-type" className={sel} value={acompteType} onChange={(e) => setAcompteType(e.target.value)}>
                <option value="aucun">Aucun</option>
                <option value="pourcent">Pourcentage</option>
                <option value="montant">Montant fixe</option>
              </select>
            </div>
            {acompteType !== "aucun" && (
              <div>
                <Label>{acompteType === "pourcent" ? "Pourcentage (%)" : "Montant (€)"}</Label>
                <Input type="number" min="0" className="mt-1.5" value={acompteValue} onChange={(e) => setAcompteValue(e.target.value)}
                  data-testid="quote-acompte-value" placeholder={acompteType === "pourcent" ? "30" : "600"} />
              </div>
            )}
          </div>
          {acompteType !== "aucun" && Number(acompteValue) > 0 && (
            <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid="quote-acompte-preview">
              Acompte : <strong>{fmtEuro(acompteType === "pourcent" ? (nonAssujetti ? totalHT : totalTTC) * Number(acompteValue) / 100 : Number(acompteValue))}</strong>
              {" "}— solde restant à facturer : <strong>{fmtEuro(Math.max(0, (nonAssujetti ? totalHT : totalTTC) - (acompteType === "pourcent" ? (nonAssujetti ? totalHT : totalTTC) * Number(acompteValue) / 100 : Number(acompteValue))))}</strong>
            </p>
          )}
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 text-sm flex justify-between" data-testid="quote-total">
            <span>Total HT : <strong>{fmtEuro(totalHT)}</strong></span>
            {nonAssujetti
              ? <span className="text-neutral-500 text-xs self-center">TVA non applicable, art. 293 B du CGI</span>
              : <span>Total TTC : <strong>{fmtEuro(totalTTC)}</strong></span>}
          </div>
          <Button data-testid="quote-send" onClick={submit} disabled={loading || !estimatedMin || Number(estimatedMin) <= 0} className="w-full bg-neutral-900 hover:bg-neutral-800 text-white">
            {loading ? "Envoi…" : "Envoyer au client pour validation"}
          </Button>
          {(!estimatedMin || Number(estimatedMin) <= 0) && (
            <p className="text-xs text-center text-neutral-400" data-testid="quote-estimate-required">Le temps estimé est obligatoire pour envoyer le devis.</p>
          )}
          {savingTemplate ? (
            <div className="flex gap-2">
              <Input placeholder="Nom du modèle (ex : Pack Community Management)" value={templateName}
                onChange={(e) => setTemplateName(e.target.value)} data-testid="quote-template-name" />
              <Button variant="outline" size="sm" onClick={saveTemplate} data-testid="quote-template-save">Enregistrer</Button>
              <Button variant="ghost" size="sm" onClick={() => setSavingTemplate(false)}>Annuler</Button>
            </div>
          ) : (
            <button onClick={() => setSavingTemplate(true)} className="text-xs text-neutral-400 hover:text-neutral-700 underline mx-auto block"
              data-testid="quote-save-template">
              Enregistrer comme modèle de devis
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ================= Modale Nouvelle demande (agence) ================= */
function NewRequestModal({ open, onClose, clients, onCreated }) {
  const [f, setF] = useState({ client_id: "", service: "community", title: "", explanation: "", priority: "moyenne", deadline: "", responsible: "" });
  const [more, setMore] = useState(false);
  const [extra, setExtra] = useState({ type: "autre", network: "", desired_date: "", origin: "agence" });
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => {
    if (open) {
      setF({ client_id: "", service: "community", title: "", explanation: "", priority: "moyenne", deadline: "", responsible: "" });
      setExtra({ type: "autre", network: "", desired_date: "", origin: "agence" });
      setFiles([]); setMore(false);
    }
  }, [open]);

  const uploadFiles = async (list) => {
    setUploading(true);
    try {
      for (const file of Array.from(list || [])) {
        const data = await uploadFile(file);
        setFiles((a) => [...a, { name: file.name, url: data.url, size: file.size }]);
      }
    } catch { toast.error("Échec de l'envoi d'un fichier"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submit = async () => {
    if (!f.client_id) { toast.error("Sélectionnez un client"); return; }
    if (!f.title.trim()) { toast.error("Titre de la demande requis"); return; }
    setBusy(true);
    try {
      const r = await api.post("/client-requests", {
        ...f, title: f.title.trim(), deadline: f.deadline || null,
        ...extra, desired_date: extra.desired_date || null, attachments: files,
      });
      toast.success("Demande créée");
      onCreated(r.data);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="new-request-modal" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Nouvelle demande</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Client *</Label>
              <select className={`${sel} mt-1`} value={f.client_id} onChange={set("client_id")} data-testid="nr-client">
                <option value="">Choisir…</option>
                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs">Service</Label>
              <select className={`${sel} mt-1`} value={f.service} onChange={set("service")} data-testid="nr-service">
                {DEMANDE_SERVICES.map((s) => <option key={s.id} value={s.id}>{s.full}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Titre *</Label>
            <Input className="mt-1" value={f.title} onChange={set("title")} placeholder="Ex : Création carte de visite" data-testid="nr-title" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <textarea className={`${sel} mt-1`} rows={3} value={f.explanation} onChange={set("explanation")}
              placeholder="Brief complet de la demande…" data-testid="nr-description" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs">Priorité</Label>
              <select className={`${sel} mt-1`} value={f.priority} onChange={set("priority")} data-testid="nr-priority">
                <option value="basse">Basse</option><option value="moyenne">Moyenne</option><option value="haute">Haute</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Échéance</Label>
              <Input type="date" className="mt-1" value={f.deadline} onChange={set("deadline")} data-testid="nr-deadline" />
            </div>
            <div>
              <Label className="text-xs">Responsable</Label>
              <Input className="mt-1" value={f.responsible} onChange={set("responsible")} placeholder="Aurélie" data-testid="nr-responsible" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Fichiers</Label>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <input ref={fileRef} type="file" multiple className="hidden" onChange={(e) => uploadFiles(e.target.files)} data-testid="nr-file-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}
                className="gap-1.5 text-xs" data-testid="nr-file-btn">
                <Paperclip className="w-3.5 h-3.5" /> {uploading ? "Envoi…" : "Ajouter un fichier"}
              </Button>
              {files.map((fl, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5" data-testid={`nr-file-${i}`}>
                  {fl.name} ✓
                </span>
              ))}
            </div>
          </div>
          <button type="button" onClick={() => setMore((v) => !v)} data-testid="nr-more"
            className="text-[11px] font-semibold text-neutral-400 hover:text-neutral-700 flex items-center gap-1">
            <ChevronDown className={`w-3 h-3 transition-transform ${more ? "rotate-180" : ""}`} /> Plus d'options
          </button>
          {more && (
            <div className="grid grid-cols-2 gap-2 border border-neutral-100 rounded-xl p-3 bg-neutral-50/50">
              <div>
                <Label className="text-xs">Type de demande</Label>
                <select className={`${sel} mt-1`} value={extra.type} onChange={(e) => setExtra({ ...extra, type: e.target.value })} data-testid="nr-type">
                  {CLIENT_REQUEST_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Origine</Label>
                <select className={`${sel} mt-1`} value={extra.origin} onChange={(e) => setExtra({ ...extra, origin: e.target.value })} data-testid="nr-origin">
                  <option value="agence">Agence</option><option value="interne">Interne</option><option value="client">Client</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Réseau</Label>
                <Input className="mt-1" value={extra.network} onChange={(e) => setExtra({ ...extra, network: e.target.value })} placeholder="Instagram…" data-testid="nr-network" />
              </div>
              <div>
                <Label className="text-xs">Date souhaitée</Label>
                <Input type="date" className="mt-1" value={extra.desired_date} onChange={(e) => setExtra({ ...extra, desired_date: e.target.value })} data-testid="nr-desired" />
              </div>
            </div>
          )}
          <Button onClick={submit} disabled={busy || !f.client_id || !f.title.trim()}
            className="w-full bg-[#0B132B] hover:bg-[#1C2A4F] text-white" data-testid="nr-submit">
            {busy ? "Création…" : "Créer la demande"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* ================= Carte Kanban ================= */
function RequestCard({ r, selected, onSelect, onHistory }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const sv = demandeService(r.service);
  const pr = demandePriority(r.priority);
  return (
    <div draggable data-testid={`kb-card-${r.id}`}
      onDragStart={(e) => { e.dataTransfer.setData("text/plain", r.id); e.dataTransfer.effectAllowed = "move"; }}
      onClick={() => onSelect(r.id)}
      className={`bg-white border rounded-xl p-2.5 cursor-pointer transition-all hover:shadow-sm group ${
        selected ? "border-[#93C5FD] ring-2 ring-[#DBEAFE]" : "border-neutral-200/80 hover:border-neutral-300"}`}>
      <div className="flex items-center gap-2">
        <span className={`w-6 h-6 rounded-full text-[8px] font-bold flex items-center justify-center shrink-0 ${avatarCls(r.client_name)}`}>{initialsOf(r.client_name)}</span>
        <span className="flex-1 min-w-0 text-[11px] font-semibold text-neutral-600 truncate">{r.client_name}</span>
        {r.origin === "client" && <span title="Créée par le client" className="text-[8px] font-bold text-sky-600 bg-sky-50 border border-sky-100 rounded-full px-1.5 py-px shrink-0">Client</span>}
        <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
          <button onClick={() => setMenuOpen((v) => !v)} data-testid={`kb-card-menu-${r.id}`}
            className="p-0.5 rounded text-neutral-300 hover:text-neutral-600 hover:bg-neutral-100 opacity-0 group-hover:opacity-100 transition-all">
            <MoreHorizontal className="w-3.5 h-3.5" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-5 z-40 w-40 bg-white border border-neutral-200 rounded-xl shadow-lg py-1">
                <button onClick={() => { setMenuOpen(false); onSelect(r.id); }} data-testid={`kb-card-open-${r.id}`}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-left hover:bg-neutral-50">
                  <Eye className="w-3 h-3 text-neutral-400" /> Ouvrir la fiche
                </button>
                <button onClick={() => { setMenuOpen(false); onHistory(r); }} data-testid={`kb-card-history-${r.id}`}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] text-left hover:bg-neutral-50">
                  <History className="w-3 h-3 text-neutral-400" /> Historique
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <p className="text-xs font-bold text-[#0F172A] mt-1.5 leading-snug">{r.title}</p>
      <span className={`inline-block mt-1 text-[9px] font-semibold border rounded-full px-2 py-px ${sv.cls}`}>{sv.label}</span>
      {r.explanation && <p className="text-[10px] text-neutral-400 mt-1 leading-snug line-clamp-2">{r.explanation}</p>}
      <div className="flex items-center justify-between mt-1.5">
        <span className="text-[9px] text-neutral-400">{fmtD(r.created_at, false)}</span>
        <span className={`text-[9px] font-semibold border rounded-full px-2 py-px ${pr.cls}`}>{pr.label}</span>
      </div>
    </div>
  );
}

/* ================= Panneau détail ================= */
function DetailPanel({ r, detail, onClose, onChanged, onQuote, onHistory, move }) {
  const navigate = useNavigate();
  const [desc, setDesc] = useState(null);
  const [resp, setResp] = useState(null);
  const [comment, setComment] = useState("");
  const [internal, setInternal] = useState(true);
  const [planMenu, setPlanMenu] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const fileRef = useRef(null);
  const descFor = useRef(null);

  useEffect(() => {
    if (!planMenu) return;
    const h = (e) => e.key === "Escape" && setPlanMenu(false);
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [planMenu]);

  const description = desc !== null && descFor.current === r.id ? desc : (r.explanation || "");
  const responsible = resp !== null && descFor.current === r.id ? resp : (r.responsible || "");
  const patch = async (fields, msgToast) => {
    try {
      await api.patch(`/client-requests/${r.id}`, fields);
      if (msgToast) toast.success(msgToast);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const saveDesc = () => { if (desc !== null && desc !== (r.explanation || "")) patch({ explanation: desc }, "Description enregistrée"); };
  const saveResp = () => { if (resp !== null && resp !== (r.responsible || "")) patch({ responsible: resp }); };

  const accept = async () => {
    try { await api.post(`/client-requests/${r.id}/accept`); toast.success("Demande acceptée — tâche créée dans la To Do"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const refuse = async () => {
    try { await api.post(`/client-requests/${r.id}/refuse`, {}); toast.success("Demande refusée"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const terminate = async () => {
    try { await api.patch(`/client-requests/${r.id}`, { action: "terminer" }); toast.success("Demande terminée — créneaux agenda libérés"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const resendQuote = async () => {
    try {
      const { data } = await api.post(`/client-requests/${r.id}/quote/resend`);
      toast.success(data.email_sent ? "Devis renvoyé au client (email envoyé)" : "Devis renvoyé au client");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const sendComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/client-requests/${r.id}/comments`, { text: comment.trim(), internal });
      setComment("");
      toast.success(internal ? "Note interne ajoutée" : "Commentaire visible par le client publié");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  const uploadFile = async (file) => {
    if (!file) return;
    try {
      const up = await uploadFile(file);
      await api.post(`/client-requests/${r.id}/attachments`, { name: file.name, url: up.url, size: file.size });
      toast.success("Fichier ajouté");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const delAttachment = async (a) => {
    if (!window.confirm(`Supprimer le fichier « ${a.name} » ?`)) return;
    try { await api.delete(`/client-requests/${r.id}/attachments/${a.id}`); toast.success("Fichier supprimé"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const traiter = async () => {
    try {
      if (r.linked_project) { navigate(projectUrl(r.linked_project)); return; }
      if (r.service === "graphisme") {
        const det = r.details || {};
        const res = await api.post("/graphisme/projects", {
          client_id: r.client_id, client_request_id: r.id, name: r.title,
          description: r.explanation || "", deadline: (r.deadline || "").slice(0, 10),
          types: det.creation_type ? [det.creation_type] : [],
          dimensions: det.dimensions || "", formats: det.format || "",
          qty_creations: det.quantity ? +det.quantity : undefined,
        });
        toast.success("Projet Graphisme créé et pré-rempli depuis la demande");
        onChanged(); navigate(`/graphisme/projet/${res.data.id}`);
      } else if (r.service === "web") {
        const res = await api.post("/web/projects", {
          client_id: r.client_id, name: r.title, description: r.explanation || "",
          delivery_date: (r.deadline || "").slice(0, 10) || undefined,
        });
        await api.post(`/client-requests/${r.id}/link-project`, { kind: "web", id: res.data.id, label: res.data.name || r.title });
        toast.success("Projet Web créé et lié");
        onChanged(); navigate(`/sites-web/projet/${res.data.id}`);
      } else {
        let taskId = r.task_id;
        if (r.status === "nouvelle") {
          const res = await api.post(`/client-requests/${r.id}/accept`);
          taskId = res.data?.task_id;
        }
        await api.post(`/client-requests/${r.id}/link-project`, { kind: "cm", id: taskId || "", label: "Tâche To Do" });
        toast.success("Tâche To Do liée à la demande");
        onChanged(); navigate("/taches");
      }
    } catch (e) { toast.error(apiError(e)); }
  };

  const agendaLink = () => {
    const blocks = detail?.blocks || [];
    if (!blocks.length) return "/agenda";
    const today = new Date().toISOString().slice(0, 10);
    const upcoming = blocks.filter((b) => b.date >= today)
      .sort((a, b) => (a.date + String(a.start_min).padStart(4, "0")).localeCompare(b.date + String(b.start_min).padStart(4, "0")));
    const target = upcoming[0] || blocks[blocks.length - 1];
    return `/agenda?week=${mondayISO(target.date)}&highlight=${target.id}`;
  };

  const sv = demandeService(r.service);
  const pr = demandePriority(r.priority);
  const st = clientRequestStatus(r.status);
  const atts = r.attachments || [];
  const comments = r.comments || [];
  const planned = (detail?.blocks || []).reduce((a, b) => a + b.duration_minutes, 0);

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm flex flex-col overflow-hidden min-h-0" data-testid="dpanel">
      {/* En-tête */}
      <div className="px-4 pt-3.5 pb-3 border-b border-neutral-100 shrink-0">
        <div className="flex items-center gap-2.5">
          <span className={`w-9 h-9 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${avatarCls(r.client_name)}`}>{initialsOf(r.client_name)}</span>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-[#0F172A] truncate" data-testid="dpanel-client">{r.client_name}</p>
            <Link to={`/clients/${r.client_id}`} className="text-[10px] font-semibold text-[#0369A1] hover:underline" data-testid="dpanel-goto-client">Voir le client →</Link>
          </div>
          <button onClick={onClose} data-testid="dpanel-close" className="p-1.5 rounded-lg text-neutral-400 hover:bg-neutral-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <h3 className="font-heading font-bold text-base text-[#0F172A] mt-2.5 leading-snug" data-testid="dpanel-title">{r.title}</h3>
        <div className="flex items-center gap-1.5 mt-2 flex-wrap">
          <select value={r.kanban_status} onChange={(e) => move(r, e.target.value)} data-testid="dpanel-status"
            className={`appearance-none cursor-pointer text-[10px] font-semibold border rounded-full pl-2.5 pr-2 py-1 focus:outline-none ${kanbanCol(r.kanban_status).head}`}>
            {KANBAN_COLS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <span className={`text-[9px] font-semibold border rounded-full px-2 py-0.5 ${st.cls}`} data-testid="dpanel-wf-status" title="Statut métier (workflow)">{st.label}</span>
          {r.origin === "client" && <span className="text-[9px] font-semibold border rounded-full px-2 py-0.5 bg-sky-50 text-sky-700 border-sky-200" data-testid="dpanel-origin">Créée par le client</span>}
        </div>
      </div>

      {/* Contenu scrollable */}
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-4">
        {/* Infos */}
        <div className="grid grid-cols-2 gap-x-3 gap-y-2.5" data-testid="dpanel-infos">
          <div>
            <p className="text-[10px] text-neutral-400 flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Date de la demande</p>
            <p className="text-xs font-semibold text-[#0F172A] mt-0.5">{fmtD(r.created_at)}</p>
          </div>
          <div>
            <p className="text-[10px] text-neutral-400 flex items-center gap-1"><Flag className="w-3 h-3" /> Priorité</p>
            <select value={r.priority} onChange={(e) => patch({ priority: e.target.value }, "Priorité mise à jour")} data-testid="dpanel-priority"
              className={`mt-0.5 appearance-none cursor-pointer text-[10px] font-semibold border rounded-full pl-2 pr-1.5 py-0.5 focus:outline-none ${pr.cls}`}>
              <option value="basse">Basse</option><option value="moyenne">Moyenne</option><option value="haute">Haute</option>
            </select>
          </div>
          <div>
            <p className="text-[10px] text-neutral-400 flex items-center gap-1"><Inbox className="w-3 h-3" /> Service</p>
            <select value={r.service} onChange={(e) => patch({ service: e.target.value }, "Service mis à jour")} data-testid="dpanel-service"
              className={`mt-0.5 appearance-none cursor-pointer text-[10px] font-semibold border rounded-full pl-2 pr-1.5 py-0.5 focus:outline-none ${sv.cls}`}>
              {DEMANDE_SERVICES.map((s) => <option key={s.id} value={s.id}>{s.full}</option>)}
            </select>
          </div>
          <div>
            <p className="text-[10px] text-neutral-400 flex items-center gap-1"><UserRound className="w-3 h-3" /> Responsable</p>
            <input value={responsible} data-testid="dpanel-responsible"
              onChange={(e) => { descFor.current = r.id; setResp(e.target.value); }} onBlur={saveResp}
              placeholder="—" className="mt-0.5 text-xs font-semibold text-[#0F172A] bg-transparent w-full focus:outline-none focus:border-b focus:border-neutral-300" />
          </div>
          <div>
            <p className="text-[10px] text-neutral-400">Échéance</p>
            <input type="date" value={(r.deadline || "").slice(0, 10)} onChange={(e) => patch({ deadline: e.target.value || null }, "Échéance mise à jour")}
              data-testid="dpanel-deadline" className="mt-0.5 text-xs font-semibold text-[#0F172A] bg-transparent focus:outline-none" />
          </div>
          <div>
            <p className="text-[10px] text-neutral-400">Type</p>
            <p className="text-xs font-semibold text-[#0F172A] mt-0.5 truncate">{clientRequestTypeLabel(r.type)}</p>
          </div>
        </div>

        {/* Description */}
        <section>
          <h4 className="text-xs font-bold text-[#0F172A] mb-1">Description</h4>
          <textarea value={description} rows={3} data-testid="dpanel-description"
            onChange={(e) => { descFor.current = r.id; setDesc(e.target.value); }} onBlur={saveDesc}
            placeholder="Brief de la demande… (modifiable)"
            className="w-full text-xs text-neutral-600 leading-relaxed bg-transparent rounded-lg px-2 py-1.5 -ml-2 hover:bg-neutral-50 focus:bg-white focus:border focus:border-neutral-200 focus:outline-none resize-y transition-colors" />
        </section>

        {/* Fichiers joints */}
        <section>
          <div className="flex items-center gap-2 mb-1.5">
            <h4 className="text-xs font-bold text-[#0F172A] flex items-center gap-1.5"><Paperclip className="w-3.5 h-3.5 text-neutral-400" /> Fichiers joints ({atts.length})</h4>
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => uploadFile(e.target.files?.[0])} data-testid="dpanel-file-input" />
            <button onClick={() => fileRef.current?.click()} className="ml-auto text-[10px] font-semibold text-[#6D28D9] hover:underline" data-testid="dpanel-file-add">
              Ajouter un fichier
            </button>
          </div>
          {atts.length === 0 && <p className="text-[11px] text-neutral-400">Aucun fichier joint.</p>}
          <div className="space-y-1">
            {atts.map((a, i) => (
              <div key={a.id || i} className="flex items-center gap-2 py-1 group" data-testid={`dpanel-file-${a.id || i}`}>
                <span className="w-7 h-7 rounded-lg bg-red-50 text-red-500 flex items-center justify-center shrink-0"><FileIcon className="w-3.5 h-3.5" /></span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[11px] font-semibold text-[#0F172A] truncate">{a.name}</span>
                  {a.size ? <span className="block text-[9px] text-neutral-400">{fmtSize(a.size)}</span> : null}
                </span>
                <a href={a.url} target="_blank" rel="noreferrer" className="p-1 text-neutral-300 hover:text-neutral-700" title="Ouvrir" data-testid={`dpanel-file-open-${a.id || i}`}><Eye className="w-3.5 h-3.5" /></a>
                <a href={a.url} download={a.name} className="p-1 text-neutral-300 hover:text-neutral-700" title="Télécharger" data-testid={`dpanel-file-dl-${a.id || i}`}><Download className="w-3.5 h-3.5" /></a>
                {a.id && (
                  <button onClick={() => delAttachment(a)} className="p-1 text-neutral-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all" title="Supprimer" data-testid={`dpanel-file-del-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>
          {atts.length > 1 && (
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/client-requests/${r.id}/attachments.zip`}
              className="inline-flex items-center gap-1 text-[10px] font-semibold text-neutral-500 hover:text-neutral-900 mt-1" data-testid="dpanel-download-all">
              <Download className="w-3 h-3" /> Tout télécharger (ZIP)
            </a>
          )}
        </section>

        {/* Planification (si devis accepté / tâche) */}
        {detail && (detail.quote || detail.task || (detail.blocks || []).length > 0) && (
          <section className="border border-neutral-100 rounded-xl p-3 bg-neutral-50/60" data-testid="dpanel-planning">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-neutral-400 mb-2">Planification</p>
            <div className="grid grid-cols-2 gap-2 text-[11px]">
              <div><p className="text-neutral-400 text-[10px]">Temps estimé</p><p className="font-semibold" data-testid="dpanel-estimated">{detail.quote?.estimated_minutes ? fmtMin(detail.quote.estimated_minutes) : "—"}</p></div>
              <div><p className="text-neutral-400 text-[10px]">Temps planifié</p><p className="font-semibold" data-testid="dpanel-planned">{fmtMin(planned)}</p></div>
              <div><p className="text-neutral-400 text-[10px]">Statut client</p><p className="font-semibold">{CLIENT_STATUS_LABELS[r.client_status] || "—"}</p></div>
              {detail.quote && <div><p className="text-neutral-400 text-[10px]">Devis</p><p className="font-semibold">{Number(detail.quote.total_ttc || 0).toFixed(2)} € {detail.quote.tva_mode === "non_assujetti" ? "HT" : "TTC"}</p></div>}
            </div>
            {(detail.blocks || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {detail.blocks.map((b) => (
                  <span key={b.id} className="text-[9px] bg-white border border-neutral-200 rounded-md px-1.5 py-0.5" data-testid={`dpanel-slot-${b.id}`}>
                    {format(parseISO(b.date), "EEE d MMM", { locale: fr })} · {String(Math.floor(b.start_min / 60)).padStart(2, "0")}:{String(b.start_min % 60).padStart(2, "0")}
                  </span>
                ))}
              </div>
            )}
            {detail.quote && (
              <button onClick={() => setQuoteOpen((v) => !v)} className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold text-[#0369A1] hover:underline" data-testid="dpanel-quote-toggle">
                <Eye className="w-3 h-3" /> {quoteOpen ? "Masquer le devis" : "Voir le devis"}
              </button>
            )}
            {quoteOpen && detail.quote && (
              <div className="mt-1.5 border-t border-neutral-200 pt-2 space-y-0.5" data-testid="dpanel-quote">
                {(detail.quote.items || []).map((i, k) => (
                  <p key={k} className="text-[10px] text-neutral-600">{i.prestation} — {i.qty} × {i.price_ht} € HT</p>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Actions workflow */}
        <section className="flex flex-wrap gap-1.5" data-testid="dpanel-actions">
          {r.status === "nouvelle" && (
            <>
              <button onClick={accept} data-testid="dpanel-accept" className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2.5 py-1.5 hover:bg-emerald-100 transition-colors">
                <CheckCircle2 className="w-3.5 h-3.5" /> Accepter → To Do
              </button>
              <button onClick={() => onQuote(r)} data-testid="dpanel-quote-btn" className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-[#0B132B] rounded-lg px-2.5 py-1.5 hover:bg-[#1C2A4F] transition-colors">
                <Euro className="w-3.5 h-3.5" /> Chiffrer
              </button>
              <button onClick={refuse} data-testid="dpanel-refuse" className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 border border-red-200 rounded-lg px-2.5 py-1.5 hover:bg-red-50 transition-colors">
                <XCircle className="w-3.5 h-3.5" /> Refuser
              </button>
            </>
          )}
          {r.status === "devis_refuse" && (
            <>
              <button onClick={() => onQuote(r)} data-testid="dpanel-quote-btn" className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-[#0B132B] rounded-lg px-2.5 py-1.5 hover:bg-[#1C2A4F] transition-colors">
                <Euro className="w-3.5 h-3.5" /> Nouvelle contre-proposition
              </button>
              <button onClick={refuse} data-testid="dpanel-refuse" className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 border border-red-200 rounded-lg px-2.5 py-1.5 hover:bg-red-50 transition-colors">
                <XCircle className="w-3.5 h-3.5" /> Clôturer la demande
              </button>
            </>
          )}
          {r.status === "devis_accepte" && (
            <button onClick={terminate} data-testid="dpanel-terminate" className="inline-flex items-center gap-1 text-[11px] font-semibold text-white bg-[#0B132B] rounded-lg px-2.5 py-1.5 hover:bg-[#1C2A4F] transition-colors">
              <CheckCircle2 className="w-3.5 h-3.5" /> Marquer terminée
            </button>
          )}
          {r.status === "devis_envoye" && (
            <button onClick={resendQuote} data-testid="dpanel-resend" className="inline-flex items-center gap-1 text-[11px] font-semibold text-neutral-600 border border-neutral-200 rounded-lg px-2.5 py-1.5 hover:bg-neutral-50 transition-colors">
              <Send className="w-3.5 h-3.5" /> Renvoyer le devis
            </button>
          )}
          <button onClick={() => onHistory(r)} data-testid="dpanel-history" className="inline-flex items-center gap-1 text-[11px] font-semibold text-neutral-500 border border-neutral-200 rounded-lg px-2.5 py-1.5 hover:bg-neutral-50 transition-colors">
            <History className="w-3.5 h-3.5" /> Historique
          </button>
        </section>

        {/* Commentaires */}
        <section>
          <h4 className="text-xs font-bold text-[#0F172A] mb-2 flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5 text-neutral-400" /> Commentaires ({comments.length})
          </h4>
          {comments.length === 0 && <p className="text-[11px] text-neutral-400" data-testid="dpanel-comments-empty">Aucun commentaire pour le moment.</p>}
          <div className="space-y-2.5">
            {comments.map((m) => (
              <div key={m.id} className="flex items-start gap-2.5" data-testid={`dpanel-comment-${m.id}`}>
                <span className={`w-7 h-7 rounded-full text-[9px] font-bold flex items-center justify-center shrink-0 ${m.by_client ? "bg-emerald-100 text-emerald-700" : avatarCls(m.by)}`}>{initialsOf(m.by)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-neutral-400">
                    <b className="text-neutral-700">{m.by}</b> · {relTime(m.at)} ·{" "}
                    <span className={`font-semibold ${m.internal ? "text-amber-700" : "text-blue-700"}`}>{m.internal ? "Interne DGA" : "Visible par le client"}</span>
                  </p>
                  <p className="text-xs text-neutral-600 whitespace-pre-line mt-0.5">{m.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="border border-neutral-200 rounded-xl p-2.5 space-y-2 mt-2.5">
            <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="dpanel-comment-input"
              placeholder="Écrire un commentaire…" className="w-full text-xs focus:outline-none resize-y" />
            <div className="flex items-center gap-2">
              <button onClick={() => setInternal(true)} data-testid="dpanel-comment-internal"
                className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${internal ? "bg-amber-100 border-amber-300 text-amber-800" : "border-neutral-200 text-neutral-400"}`}>
                Interne DGA
              </button>
              <button onClick={() => setInternal(false)} data-testid="dpanel-comment-client"
                className={`text-[10px] font-semibold rounded-full px-2.5 py-1 border transition-colors ${!internal ? "bg-blue-100 border-blue-300 text-blue-800" : "border-neutral-200 text-neutral-400"}`}>
                Visible par le client
              </button>
              <button onClick={sendComment} disabled={!comment.trim()} data-testid="dpanel-comment-send"
                className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold bg-[#0B132B] text-white rounded-lg px-3 py-1.5 hover:bg-[#1C2A4F] disabled:opacity-50 transition-colors">
                <Send className="w-3 h-3" /> Envoyer
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Pied : traiter + planifier */}
      <div className="px-4 py-3 border-t border-neutral-100 shrink-0 space-y-2">
        {r.linked_project && (
          <Link to={projectUrl(r.linked_project)} data-testid="dpanel-linked-project"
            className="flex items-center gap-2 text-[11px] font-semibold text-[#6D28D9] bg-violet-50 border border-violet-200 rounded-xl px-3 py-2 hover:bg-violet-100 transition-colors">
            <Link2 className="w-3.5 h-3.5" /> Projet lié : {r.linked_project.label} <ArrowRight className="w-3 h-3 ml-auto" />
          </Link>
        )}
        <div className="flex gap-2">
          <Button onClick={traiter} data-testid="dpanel-traiter"
            className="flex-1 bg-[#1D4ED8] hover:bg-[#1E40AF] text-white gap-1.5 text-xs">
            {r.linked_project ? "Voir le projet" : "Traiter la demande"} <ArrowRight className="w-3.5 h-3.5" />
          </Button>
          <div className="relative">
            <Button variant="outline" onClick={() => setPlanMenu((v) => !v)} data-testid="dpanel-planifier"
              className="gap-1.5 text-xs">
              <CalendarRange className="w-3.5 h-3.5" /> Planifier
            </Button>
            {planMenu && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setPlanMenu(false)} />
                <div className="absolute right-0 bottom-10 z-40 w-52 bg-white border border-neutral-200 rounded-xl shadow-lg py-1" data-testid="dpanel-plan-menu">
                  <button onClick={() => { setPlanMenu(false); navigate(agendaLink()); }} data-testid="dpanel-plan-agenda"
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-neutral-50">
                    <CalendarRange className="w-3.5 h-3.5 text-neutral-400" /> Voir dans l'agenda
                  </button>
                  {detail?.task && (
                    <button onClick={() => { setPlanMenu(false); navigate("/taches"); }} data-testid="dpanel-plan-task"
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-neutral-50">
                      <ListTodo className="w-3.5 h-3.5 text-neutral-400" /> Voir la tâche To Do
                    </button>
                  )}
                  {r.status === "nouvelle" && (
                    <button onClick={() => { setPlanMenu(false); accept(); }} data-testid="dpanel-plan-create-task"
                      className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-neutral-50">
                      <Plus className="w-3.5 h-3.5 text-neutral-400" /> Créer une tâche (accepter)
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ================= Page Demandes — Kanban ================= */
export default function Demandes() {
  const [requests, setRequests] = useState([]);
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ q: "", client_id: "", service: "", status: "", date: "" });
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [quoteFor, setQuoteFor] = useState(null);
  const [history, setHistory] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [dragCol, setDragCol] = useState(null);
  const [colLimit, setColLimit] = useState({});
  const [searchParams] = useSearchParams();
  const focus = searchParams.get("focus");

  const load = () => api.get("/client-requests").then((r) => setRequests(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { api.get("/clients").then((r) => setClients((r.data || []).filter((c) => !c.archived))).catch(() => {}); }, []);
  useEffect(() => {
    api.post("/client-requests/mark-seen")
      .then(() => window.dispatchEvent(new Event("sf:demandes"))).catch(() => {});
  }, []);
  useEffect(() => { if (focus && requests.length) setSelectedId(focus); }, [focus, requests.length]); // eslint-disable-line
  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    api.get(`/client-requests/${selectedId}/detail`).then((r) => setDetail(r.data)).catch(() => setDetail(null));
  }, [selectedId, requests]); // eslint-disable-line

  const move = async (r, col) => {
    if (!col || r.kanban_status === col) return;
    const prev = r.kanban_status;
    setRequests((arr) => arr.map((x) => (x.id === r.id ? { ...x, kanban_status: col } : x)));
    try {
      if (col === "a_traiter" && r.status === "nouvelle") {
        await api.post(`/client-requests/${r.id}/accept`);
        toast.success("Demande acceptée — tâche créée dans la To Do");
      } else if (col === "finalisee" && ["acceptee", "devis_accepte"].includes(r.status)) {
        await api.patch(`/client-requests/${r.id}`, { action: "terminer" });
        toast.success("Demande terminée — créneaux agenda libérés");
      } else {
        await api.patch(`/client-requests/${r.id}`, { kanban_status: col });
        toast.success(`Déplacée vers « ${kanbanCol(col).label} »`, {
          action: { label: "Annuler", onClick: async () => {
            await api.patch(`/client-requests/${r.id}`, { kanban_status: prev }).catch(() => {});
            load();
          } },
        });
      }
    } catch (e) { toast.error(apiError(e)); }
    load();
  };

  const openHistory = async (r) => {
    const { data } = await api.get(`/client-requests/${r.id}/history`);
    setHistory({ request: r, rows: data });
  };

  const filtered = useMemo(() => {
    let list = requests;
    if (filters.client_id) list = list.filter((r) => r.client_id === filters.client_id);
    if (filters.service) list = list.filter((r) => r.service === filters.service);
    if (filters.status) list = list.filter((r) => r.kanban_status === filters.status);
    if (filters.date) {
      const today = new Date().toISOString().slice(0, 10);
      if (filters.date === "today") list = list.filter((r) => (r.created_at || "").slice(0, 10) === today);
      else if (filters.date === "7d") {
        const from = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
        list = list.filter((r) => (r.created_at || "").slice(0, 10) >= from);
      } else if (filters.date === "month") list = list.filter((r) => (r.created_at || "").slice(0, 7) === today.slice(0, 7));
    }
    if (filters.q.trim()) {
      const q = filters.q.trim().toLowerCase();
      list = list.filter((r) => [r.title, r.client_name, r.explanation].some((s) => (s || "").toLowerCase().includes(q)));
    }
    return list;
  }, [requests, filters]);

  const byCol = useMemo(() => Object.fromEntries(
    KANBAN_COLS.map((c) => [c.id, filtered.filter((r) => r.kanban_status === c.id)])), [filtered]);
  const activeFilters = Object.values(filters).filter(Boolean).length;
  const selected = requests.find((r) => r.id === selectedId) || null;

  return (
    <div className="flex flex-col gap-3 h-[calc(100vh-7.5rem)] overflow-hidden" data-testid="demandes-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 shrink-0">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">Demandes <Inbox className="w-5 h-5 text-[#1D4ED8]" /></h1>
          <p className="text-neutral-500 text-xs mt-0.5">Centralisez toutes les demandes clients et suivez leur avancement.</p>
        </div>
        <div className="flex items-center gap-2">
          <p className="hidden 2xl:block text-right text-xs italic text-neutral-400 leading-snug mr-2">« Des demandes bien organisées,<br />des clients bien accompagnés. »</p>
          <Button size="sm" onClick={() => setModalOpen(true)} className="bg-[#1D4ED8] hover:bg-[#1E40AF] text-white gap-1.5" data-testid="new-request-btn">
            <Plus className="w-3.5 h-3.5" /> Nouvelle demande
          </Button>
        </div>
      </div>

      {/* Barre de filtres — une seule ligne */}
      <div className="flex items-center gap-2 shrink-0" data-testid="demandes-filters">
        <div className="relative w-64">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input className="pl-8 h-8 text-xs rounded-lg" placeholder="Rechercher une demande…" value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="demande-search" />
        </div>
        <select className="h-8 text-xs border border-neutral-200 rounded-lg px-2 bg-white focus:outline-none w-40" value={filters.client_id}
          onChange={(e) => setFilters({ ...filters, client_id: e.target.value })} data-testid="demande-filter-client">
          <option value="">Tous les clients</option>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="h-8 text-xs border border-neutral-200 rounded-lg px-2 bg-white focus:outline-none w-40" value={filters.service}
          onChange={(e) => setFilters({ ...filters, service: e.target.value })} data-testid="demande-filter-service">
          <option value="">Tous les services</option>
          {DEMANDE_SERVICES.map((s) => <option key={s.id} value={s.id}>{s.full}</option>)}
        </select>
        <select className="h-8 text-xs border border-neutral-200 rounded-lg px-2 bg-white focus:outline-none w-36" value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="demande-filter-status">
          <option value="">Tous les statuts</option>
          {KANBAN_COLS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select className="h-8 text-xs border border-neutral-200 rounded-lg px-2 bg-white focus:outline-none w-36" value={filters.date}
          onChange={(e) => setFilters({ ...filters, date: e.target.value })} data-testid="demande-filter-date">
          <option value="">Toutes les dates</option>
          <option value="today">Aujourd'hui</option>
          <option value="7d">7 derniers jours</option>
          <option value="month">Ce mois</option>
        </select>
        {activeFilters > 0 && (
          <button onClick={() => setFilters({ q: "", client_id: "", service: "", status: "", date: "" })}
            className="text-[11px] font-semibold text-[#0369A1] hover:underline ml-auto" data-testid="demande-filters-reset">
            Réinitialiser
          </button>
        )}
      </div>

      {/* Kanban + panneau détail */}
      <div className={`flex-1 min-h-0 grid gap-3 ${selected ? "grid-cols-1 xl:grid-cols-[minmax(0,1fr)_400px]" : "grid-cols-1"}`}>
        <div className="min-h-0 overflow-x-auto" data-testid="kanban">
          <div className={`h-full min-h-0 flex gap-2.5 ${selected ? "" : ""}`}>
            {KANBAN_COLS.map((col) => (
              <div key={col.id}
                onDragOver={(e) => { e.preventDefault(); setDragCol(col.id); }}
                onDragLeave={() => setDragCol((c) => (c === col.id ? null : c))}
                onDrop={(e) => {
                  e.preventDefault(); setDragCol(null);
                  const rid = e.dataTransfer.getData("text/plain");
                  const r = requests.find((x) => x.id === rid);
                  if (r) move(r, col.id);
                }}
                className={`flex flex-col min-h-0 rounded-2xl border transition-shadow ${selected ? "w-[228px] shrink-0" : "flex-1 min-w-[210px]"} ${col.head} ${dragCol === col.id ? `ring-2 ${col.ring}` : ""}`}
                data-testid={`kb-col-${col.id}`}>
                <div className="flex items-center gap-1.5 px-3 py-2.5 shrink-0 sticky top-0">
                  <h2 className="font-heading font-bold text-[13px] text-[#0F172A]">{col.label}</h2>
                  <span className="text-[10px] font-bold text-neutral-400" data-testid={`kb-count-${col.id}`}>{byCol[col.id].length}</span>
                  <button onClick={() => setModalOpen(true)} className="ml-auto p-0.5 rounded text-neutral-400 hover:text-neutral-700 hover:bg-white/60 transition-colors"
                    title="Nouvelle demande" data-testid={`kb-add-${col.id}`}>
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                </div>
                <div className="flex-1 min-h-0 overflow-y-auto px-2 pb-2 space-y-2" data-testid={`kb-list-${col.id}`}>
                  {byCol[col.id].slice(0, colLimit[col.id] || 15).map((r) => (
                    <RequestCard key={r.id} r={r} selected={selectedId === r.id}
                      onSelect={(id) => setSelectedId((cur) => (cur === id ? null : id))}
                      onHistory={openHistory} />
                  ))}
                  {byCol[col.id].length > (colLimit[col.id] || 15) && (
                    <button onClick={() => setColLimit((m) => ({ ...m, [col.id]: (m[col.id] || 15) + 50 }))}
                      className="w-full text-[10px] font-semibold text-neutral-500 hover:text-neutral-900 border border-dashed border-neutral-300/70 rounded-lg py-1.5 transition-colors"
                      data-testid={`kb-more-${col.id}`}>
                      Voir plus ({byCol[col.id].length - (colLimit[col.id] || 15)})
                    </button>
                  )}
                  {byCol[col.id].length === 0 && (
                    <div className="border border-dashed border-neutral-300/70 rounded-xl p-4 text-center text-[10px] text-neutral-400">
                      Aucune demande
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {selected && (
          <DetailPanel r={selected} detail={detail} move={move}
            onClose={() => setSelectedId(null)} onChanged={load}
            onQuote={setQuoteFor} onHistory={openHistory} />
        )}
      </div>

      <NewRequestModal open={modalOpen} onClose={() => setModalOpen(false)} clients={clients}
        onCreated={(r) => { load(); setSelectedId(r.id); }} />
      <QuoteModal request={quoteFor} onClose={() => setQuoteFor(null)} onSent={load} />

      <Dialog open={!!history} onOpenChange={() => setHistory(null)}>
        <DialogContent className="max-w-md" data-testid="demande-history-modal" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Historique — {history?.request?.title}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {(history?.rows || []).map((h) => (
              <div key={h.id} className="text-sm flex gap-2">
                <span className="text-neutral-400 shrink-0 text-xs mt-0.5">{format(parseISO(h.created_at), "d MMM HH:mm", { locale: fr })}</span>
                <span><strong>{h.actor}</strong> — {h.action}</span>
              </div>
            ))}
            {(history?.rows || []).length === 0 && <p className="text-sm text-neutral-400">Aucun historique.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
